import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  Subject,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { IServiceChangeRequest } from '../models/service-change-request.model';
import {
  IHttpPagedResponse,
  IHttpSingleDataResponse,
  IHttpUnpagedResponse,
  RolesEnum,
} from '@irembo-andela/irembogov3-common';
import { IServiceCategory } from '../models/service-category.model';
import { IConfigStepChangeRequestMetaData } from '../models/service-change-request-config-steps-forms.model';
import { IWorkflowTemplate } from '../models/workflow-templates.model';
import { IServiceCurrency } from '../models/service-currency.model';
import { ISaveServiceChangeRequestReviewIssueRequest } from '../models/http/save-service-change-request-review-issue-request.model';
import { IServiceChangeRequestReviewIssue } from '../models/service-change-request-review-issue.model';
import { IServicePaymentMerchant } from '../models/http/service-payment-mercant.model';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { IServiceLanguage } from '../models/service-language.model';
import { IIntegrationEndpoint } from '../models/http/integration-endpoints.model';
import { IInternalDataset } from '../models/internal-dataset.model';
import { ICertificateTemplate } from '../models/certificate-template-model';
import { OfficeResponse } from '../../modules/services/configuration-steps/office-assignment/office-assignment-config/office.model';
import { IPriceLogic } from '../models/price-logic.model';
import { IPriceLogicParameter } from '../models/price-logic-parameter.model';
import {
  NotificationTemplateMutationResponse,
  NotificationTemplateStatus,
  NotificationTemplateType,
  NotificationTemplate,
  ServiceNotificationTemplates,
} from '../models/notification.model';
import { IIremboOrganization } from '../models/irembo-organization.model';
import { IService } from '../models/service.model';
import { JourneyV3Model } from '../models/journey.model';

interface IFileDto {
  contentType: string;
  file: string;
}

export interface ImportTemplateRequest {
  organizationId: string;
  templateType: 'HTML';
  templateName: string;
  templateLanguage: string;
  template: File; // Changed from string to File for multipart upload
}

export interface ServiceFormField {
  fieldName: string;
  fieldLabel: string;
  fieldType: string;
  category: 'regular' | 'attachment';
  section?: string;
}

export interface FieldSelectionState {
  requestId: string | null;
  allFields: ServiceFormField[];
  attachmentFields: ServiceFormField[];
  selectedFields: string[];
  loading: boolean;
  availableRequests: IServiceChangeRequest[];
}

interface CertificateCreationResponse {
  certificateCode: string;
  certificateName: string;
  description: string;
}

interface CertificateCreationRequest {
  certificateCode: string;
  certificateName: string;
  description: string;
}

export interface TemplateVariableInfo {
  name: string;
  type: string;
  defaultValue?: string;
}

export interface CreateNotificationTemplateRequestBase {
  template: string;
  language: string;
  status: NotificationTemplateStatus;
  templateName: string;
  organizationId?: string;
  templateType?: NotificationTemplateType;
}

export interface CreateEmailNotificationTemplateRequest
  extends CreateNotificationTemplateRequestBase {
  subject: string;
}

export type CreateSmsNotificationTemplateRequest =
  CreateNotificationTemplateRequestBase;

export type CreateNotificationTemplateRequest =
  | CreateEmailNotificationTemplateRequest
  | CreateSmsNotificationTemplateRequest;

export type NotificationTemplateCreateResponse =
  NotificationTemplateMutationResponse;
@Injectable({
  providedIn: 'root',
})
export class ServiceChangeRequestService {
  saveForm$ = new Subject<string>();
  serviceFormSection = new Subject();
  saveFormFields = new Subject<void>();
  requestId = new BehaviorSubject<string | null>(null);
  /** Emits whenever a review issue is created, deleted, or resolved. */
  reviewIssueChanged$ = new Subject<void>();
  DefaultLanguage: IServiceLanguage = {
    name: 'English',
    nativeName: 'English',
    locale: 'en-US',
    status: 'ACTIVE',
  } as IServiceLanguage;
  languages = new BehaviorSubject<IServiceLanguage[]>([this.DefaultLanguage]);
  private languageFetchInFlight = false;
  private languageFetchAttempted = false;

  private fieldSelectionState$ = new BehaviorSubject<FieldSelectionState>({
    requestId: null,
    allFields: [],
    attachmentFields: [],
    selectedFields: [],
    loading: false,
    availableRequests: [],
  });

  constructor(private http: HttpClient) {}

  /**
   *
   * @deprecated
   *
   */
  openDoc(data: string): void {
    const win = window.open();
    win?.document.write(
      '<iframe src="' +
        data +
        '" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>'
    );
  }

  getAssignedServiceChangeRequests(
    queryParams: string,
    role: RolesEnum.ROLE_QA_REVIEWER | RolesEnum.ROLE_SERVICE_MANAGER
  ): Observable<IHttpPagedResponse<IServiceChangeRequest>> {
    const apiEnpointForRole: string =
      role === RolesEnum.ROLE_QA_REVIEWER ? 'qa-reviewer' : 'service-manager';
    return this.http.get<IHttpPagedResponse<IServiceChangeRequest>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${apiEnpointForRole}?${queryParams}`
    );
  }

  // ITMED-113: organisations scoped to the caller's OWN assigned change requests.
  // Same paged response shape as the old /admin/v1/organizations call (page/size/name/id
  // passed straight through) — but only the caller's orgs, so no platform-wide enumeration.
  getAssignedOrganizations(
    role: RolesEnum.ROLE_QA_REVIEWER | RolesEnum.ROLE_SERVICE_MANAGER,
    queryParams: string
  ): Observable<IHttpPagedResponse<IIremboOrganization>> {
    const apiEnpointForRole: string =
      role === RolesEnum.ROLE_QA_REVIEWER ? 'qa-reviewer' : 'service-manager';
    return this.http.get<IHttpPagedResponse<IIremboOrganization>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${apiEnpointForRole}/organizations?${queryParams}`
    );
  }

  getIremboOrganizations(filterParams: string): Observable<any> {
    return this.http.get<IHttpPagedResponse<IIremboOrganization>>(
      `${environment.apiGatewayBaseUrl}/admin/v1/organizations?${filterParams}`
    );
  }

  getChangeRequestsByOrganization(
    organizationId: string,
    page: number,
    size: number,
    args = ''
  ): Observable<IHttpPagedResponse<IServiceChangeRequest>> {
    return this.http.get<IHttpPagedResponse<IServiceChangeRequest>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/organization/${organizationId}?size=${size}&page=${page}${args}`
    );
  }

  getAssignedServiceChangeRequestById(
    requestId: string
  ): Observable<IHttpSingleDataResponse<IServiceChangeRequest>> {
    return this.http.get<IHttpSingleDataResponse<IServiceChangeRequest>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}`
    );
  }

  downloadRequirementDocument(requestId: string): Observable<Blob> {
    return this.http.get(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/requirement-document`,
      { responseType: 'blob' }
    );
  }

  openRequirementDocument(requestId: string): Observable<void> {
    const viewer = window.open('', '_blank');
    if (viewer) {
      viewer.opener = null;
    }

    return this.downloadRequirementDocument(requestId).pipe(
      tap(blob => {
        const fileUrl = URL.createObjectURL(blob);
        if (viewer) {
          viewer.location.replace(fileUrl);
        } else {
          const link = document.createElement('a');
          link.href = fileUrl;
          link.target = '_blank';
          link.rel = 'noopener';
          link.click();
        }
        setTimeout(() => URL.revokeObjectURL(fileUrl), 60_000);
      }),
      map(() => undefined),
      catchError(error => {
        viewer?.close();
        return throwError(() => error);
      })
    );
  }

  getServiceChangeRequestDetails(
    requestId: string
  ): Observable<IHttpSingleDataResponse<IServiceChangeRequest>> {
    return this.http.get<IHttpSingleDataResponse<IServiceChangeRequest>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-request-details/${requestId}`
    );
  }

  updateServiceChangeRequestMetadata(
    requestId: string,
    metadata: IConfigStepChangeRequestMetaData
  ): Observable<IHttpSingleDataResponse<IServiceChangeRequest>> {
    return this.http.put<IHttpSingleDataResponse<IServiceChangeRequest>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/meta-data`,
      metadata
    );
  }

  getServiceChangeRequestFormDefinition(
    requestId: string
  ): Observable<IHttpSingleDataResponse<Record<string, FormlyFieldConfig[]>>> {
    return this.http.get<
      IHttpSingleDataResponse<Record<string, FormlyFieldConfig[]>>
    >(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/form-definition`
    );
  }

  updateServiceChangeRequestFormDefinition(
    requestId: string,
    formDefinition: Record<string, unknown>
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/form-definition`,
      formDefinition,
      { headers }
    );
  }

  getServiceChangeRequestWorkflowDefinition(
    requestId: string
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    return this.http.get<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/workflow-definition`
    );
  }

  updateServiceChangeRequestWorkflowDefinition(
    requestId: string,
    workflowDefinition: Record<string, unknown>
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/workflow-definition`,
      workflowDefinition,
      { headers }
    );
  }

  getServiceChangeRequestWorkflowDefinitionAndNextStepsAndProcessingTime(
    requestId: string
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    return this.http.get<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/workflow-next-steps-processing-time`
    );
  }

  updateServiceChangeRequestWorkflowDefinitionAndNextStepsAndProcessingTime(
    requestId: string,
    workflowDefinition: Record<string, unknown>
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/workflow-next-steps-processing-time`,
      workflowDefinition,
      { headers }
    );
  }

  updateServiceChangeRequestPricing(
    requestId: string,
    pricingData: Record<string, unknown>
  ): Observable<IHttpSingleDataResponse<IServiceChangeRequest>> {
    return this.http.put<IHttpSingleDataResponse<IServiceChangeRequest>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/pricing`,
      pricingData
    );
  }

  getServiceChangeRequestPricingDetails(
    requestId: string
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    return this.http.get<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/pricing`
    );
  }

  getServiceCategories(
    queryString: string
  ): Observable<IHttpPagedResponse<IServiceCategory>> {
    return this.http.get<IHttpPagedResponse<IServiceCategory>>(
      `${environment.apiGatewayBaseUrl}/admin/v1/categories?${queryString}`
    );
  }

  getServiceCategoryById(
    categoryId: string
  ): Observable<IHttpSingleDataResponse<IServiceCategory>> {
    return this.http.get<IHttpSingleDataResponse<IServiceCategory>>(
      `${environment.apiGatewayBaseUrl}/admin/v1/categories/${categoryId}`
    );
  }

  getServiceCurrencies(
    queryString: string
  ): Observable<IHttpPagedResponse<IServiceCurrency>> {
    return this.http.get<IHttpPagedResponse<IServiceCurrency>>(
      `${environment.apiGatewayBaseUrl}/admin/v1/currencies?${queryString}`
    );
  }

  getServiceLanguages(
    queryString: string
  ): Observable<IHttpPagedResponse<IServiceLanguage>> {
    return this.http.get<IHttpPagedResponse<IServiceLanguage>>(
      `${environment.translationSourceUrl}/admin/v1/languages?${queryString}`
    );
  }

  ensureServiceLanguagesLoaded(queryString = 'page=0&size=100'): void {
    if (this.languageFetchInFlight || this.languageFetchAttempted) return;
    this.languageFetchInFlight = true;
    this.languageFetchAttempted = true;
    this.getServiceLanguages(queryString)
      .pipe(
        map(response => response?.data?.content ?? []),
        catchError(() => of([]))
      )
      .subscribe(languages => {
        this.languageFetchInFlight = false;
        const activeLanguages = languages.filter(
          language => language.status !== 'INACTIVE'
        );
        if (activeLanguages.length) {
          this.languages.next(activeLanguages);
        }
      });
  }

  getPaymentMerchants(
    queryString: string
  ): Observable<IHttpPagedResponse<IServicePaymentMerchant>> {
    return this.http.get<IHttpPagedResponse<IServicePaymentMerchant>>(
      `${environment.apiGatewayBaseUrl}/application/v1/payment-merchants?${queryString}`
    );
  }

  getWorkflowTemplates(
    queryString: string
  ): Observable<IHttpPagedResponse<IWorkflowTemplate>> {
    return this.http.get<IHttpPagedResponse<IWorkflowTemplate>>(
      `${environment.apiGatewayBaseUrl}/admin/v1/public/workflow-templates?${queryString}`
    );
  }

  getServiceChangeRequestTranslation(
    requestId: string
  ): Observable<
    IHttpSingleDataResponse<Record<string, Record<string, unknown>>>
  > {
    return this.http.get<
      IHttpSingleDataResponse<Record<string, Record<string, unknown>>>
    >(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/translation`
    );
  }

  getServiceChangeRequestThirdPartyFormDefination(
    requestId: string
  ): Observable<
    IHttpSingleDataResponse<Record<string, Record<string, unknown>>>
  > {
    return this.http.get<
      IHttpSingleDataResponse<Record<string, Record<string, unknown>>>
    >(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/third-party-form-definition`
    );
  }

  updateServiceChangeRequestTranslation(
    requestId: string,
    translations: Record<string, unknown>
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/translation`,
      translations,
      { headers }
    );
  }

  submitChangeRequestToReviewer(
    changeRequestId: string
  ): Observable<IHttpSingleDataResponse<IServiceChangeRequest>> {
    return this.http.put<IHttpSingleDataResponse<IServiceChangeRequest>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${changeRequestId}/action/send-to-qa`,
      {}
    );
  }

  approveChangeRequestByReviewer(
    changeRequestId: string
  ): Observable<IHttpSingleDataResponse<IServiceChangeRequest>> {
    return this.http.put<IHttpSingleDataResponse<IServiceChangeRequest>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${changeRequestId}/action/ready-to-go-live`,
      {}
    );
  }

  rejectChangeRequestByReviewer(
    changeRequestId: string
  ): Observable<IHttpSingleDataResponse<IServiceChangeRequest>> {
    return this.http.put<IHttpSingleDataResponse<IServiceChangeRequest>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${changeRequestId}/action/reject-from-qa`,
      {}
    );
  }

  saveChangeRequestReviewIssue(
    request: ISaveServiceChangeRequestReviewIssueRequest
  ): Observable<IHttpSingleDataResponse<IServiceChangeRequestReviewIssue>> {
    return this.http.post<
      IHttpSingleDataResponse<IServiceChangeRequestReviewIssue>
    >(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-request-feedbacks`,
      request
    );
  }

  updateChangeRequestReviewIssue(
    request: ISaveServiceChangeRequestReviewIssueRequest
  ): Observable<IServiceChangeRequestReviewIssue> {
    return this.http.put<IServiceChangeRequestReviewIssue>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-request-feedbacks/${request.id}`,
      { title: request.title, description: request.description }
    );
  }

  getChangeRequestReviewIssues(
    queryString: string
  ): Observable<IHttpPagedResponse<IServiceChangeRequestReviewIssue>> {
    return this.http.get<IHttpPagedResponse<IServiceChangeRequestReviewIssue>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-request-feedbacks?${queryString}`
    );
  }

  deleteChangeRequestReviewIssue(
    issueId: string
  ): Observable<IServiceChangeRequestReviewIssue> {
    return this.http.delete<IServiceChangeRequestReviewIssue>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-request-feedbacks/${issueId}`,
      {}
    );
  }

  resolveChangeRequestReviewIssue(
    issueId: string
  ): Observable<IServiceChangeRequestReviewIssue> {
    return this.http.put<IServiceChangeRequestReviewIssue>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-request-feedbacks/${issueId}/resolve`,
      {}
    );
  }

  getCountryCodesWithFlags(): Observable<any> {
    const currentDataPath = '../../../assets/data/country-codes.json';
    return this.http.get<IHttpPagedResponse<any>>(currentDataPath);
  }

  getIntegrationEndpoints(
    queryStrings: string
  ): Observable<IHttpPagedResponse<IIntegrationEndpoint>> {
    return this.http.get<IHttpPagedResponse<IIntegrationEndpoint>>(
      `${environment.apiGatewayBaseUrl}/integration/v1/endpoints?${queryStrings}`,
      {}
    );
  }

  getInternalDatasets(
    queryParams: string
  ): Observable<IHttpPagedResponse<IInternalDataset>> {
    return this.http.get<IHttpPagedResponse<IInternalDataset>>(
      `${environment.apiGatewayBaseUrl}/admin/v1/datasets?${queryParams}&sortDirection=DESC`,
      {}
    );
  }

  getInternalDataset(
    datasetId: string
  ): Observable<IHttpSingleDataResponse<IInternalDataset>> {
    return this.http.get<IHttpSingleDataResponse<IInternalDataset>>(
      `${environment.apiGatewayBaseUrl}/admin/v1/datasets/${datasetId}`,
      {}
    );
  }

  getIntegrationEndpoint(
    code: string
  ): Observable<IHttpSingleDataResponse<IIntegrationEndpoint>> {
    return this.http.get<IHttpSingleDataResponse<IIntegrationEndpoint>>(
      `${environment.apiGatewayBaseUrl}/integration/v1/endpoints/${code}/get`,
      {}
    );
  }

  updateServiceChangeRequestThirdPartyFormDefinition(
    requestId: string,
    formDefinition: Record<string, Record<string, unknown>>
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/third-party-form-definition`,
      formDefinition,
      { headers }
    );
  }

  removeServiceChangeRequestThirdPartyFormDefinition(
    requestId: string
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    const headers: HttpHeaders = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${requestId}/third-party-form-definition/remove`,
      { headers }
    );
  }

  getAllServiceCategories(): Observable<
    IHttpUnpagedResponse<IServiceCategory>
  > {
    return this.http.get<IHttpUnpagedResponse<IServiceCategory>>(
      `${environment.apiGatewayBaseUrl}/admin/v1/categories/all`
    );
  }

  getCertificateTemplates(
    queryParams: string
  ): Observable<IHttpPagedResponse<ICertificateTemplate>> {
    return this.http.get<IHttpPagedResponse<ICertificateTemplate>>(
      `${environment.apiGatewayBaseUrl}/document-generation/v1/certificate-templates?${queryParams}`,
      {}
    );
  }

  getStaticTranslations(locale: string): Observable<{ [key: string]: string }> {
    return this.http
      .get<{ [key: string]: string }>(
        `${environment.translationSourceUrl}/admin/v1/translations/static-text/by-locale/${locale}`
      )
      .pipe(
        catchError(() => of({})), // Return empty object if API fails
        map(translations => translations || {})
      );
  }

  prepareForTesting(
    changeRequestId: string
  ): Observable<IHttpSingleDataResponse<IServiceChangeRequest>> {
    return this.http.post<IHttpSingleDataResponse<IServiceChangeRequest>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${changeRequestId}/action/prepare-for-testing`,
      {}
    );
  }

  getOrganizationOffices(
    queryParams: string
  ): Observable<IHttpPagedResponse<OfficeResponse>> {
    return this.http.get<IHttpPagedResponse<OfficeResponse>>(
      `${environment.apiGatewayBaseUrl}/officer/v1/offices/filter?${queryParams}`,
      {}
    );
  }

  getPriceLogics(
    organizationId: string
  ): Observable<IHttpUnpagedResponse<IPriceLogic>> {
    return this.http.get<IHttpUnpagedResponse<IPriceLogic>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/pricing/price-logic?deactivated=false`,
      { headers: { organizationId } }
    );
  }

  getPriceLogicParameters(
    priceLogicId: string
  ): Observable<IHttpUnpagedResponse<IPriceLogicParameter>> {
    return this.http.get<IHttpUnpagedResponse<IPriceLogicParameter>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/pricing/price-parameter/${priceLogicId}`,
      {}
    );
  }

  getApplicationStates(): Observable<IHttpUnpagedResponse<any>> {
    return this.http.get<IHttpUnpagedResponse<any>>(
      `${environment.apiGatewayBaseUrl}/application/v1/application-states`
    );
  }

  getAllServices(): Observable<IHttpPagedResponse<any>> {
    return this.http.get<IHttpPagedResponse<any>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/services`
    );
  }

  getServicesWithPagination(
    page = 0,
    size = 10,
    search?: string,
    sort = 'serviceName',
    sortDirection = 'asc',
    serviceType = 'NORMAL'
  ): Observable<IHttpPagedResponse<any>> {
    let params = new HttpParams()
      .set('page', 0)
      .set('size', size.toString())
      .set('sort', sort)
      .set('sortDirection', sortDirection)
      .set('serviceType', serviceType);

    if (search && search.trim()) {
      params = params.set('serviceName', search.trim());
    }

    return this.http.get<IHttpPagedResponse<any>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/services`,
      { params }
    );
  }

  getServicesByOrganization(
    organizationId: string,
    page: number,
    size: number,
    serviceName?: string,
    categoryId?: string
  ): Observable<IHttpPagedResponse<IService>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (serviceName && serviceName.trim()) {
      params = params.set('serviceName', serviceName.trim());
    }

    if (categoryId && categoryId.trim()) {
      params = params.set('categoryId', categoryId.trim());
    }

    return this.http.get<IHttpPagedResponse<IService>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/organizations/${organizationId}/services`,
      { params }
    );
  }

  getPriceFormulas(
    priceLogicId: string,
    params: { deactivated?: boolean } = {}
  ): Observable<IHttpUnpagedResponse<any>> {
    const httpParams = new HttpParams({
      fromObject: params as any,
    });

    return this.http.get<IHttpUnpagedResponse<any>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/pricing/price-formula/${priceLogicId}`,
      { params: httpParams }
    );
  }

  getJourneyServiceMapping(
    changeRequestId: string
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    return this.http.get<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${changeRequestId}/journey-mapping`
    );
  }

  updateJourneyServiceMapping(
    changeRequestId: string,
    serviceMappingData: Record<string, unknown> | JourneyV3Model
  ): Observable<IHttpSingleDataResponse<Record<string, unknown>>> {
    return this.http.put<IHttpSingleDataResponse<Record<string, unknown>>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${changeRequestId}/journey-mapping`,
      serviceMappingData
    );
  }

  getNotificationTemplates(
    queryParams?: string
  ): Observable<IHttpSingleDataResponse<NotificationTemplate[]>> {
    return this.http.get<IHttpSingleDataResponse<NotificationTemplate[]>>(
      `${environment.apiGatewayBaseUrl}/notification/v1/email/template?${queryParams}`
    );
  }

  getAllNotificationTemplates(
    queryParams?: string
  ): Observable<IHttpSingleDataResponse<ServiceNotificationTemplates>> {
    return this.http.get<IHttpSingleDataResponse<ServiceNotificationTemplates>>(
      `${environment.apiGatewayBaseUrl}/notification/v1/templates?${queryParams}`
    );
  }

  getSmsNotificationTemplates(
    queryParams?: string
  ): Observable<IHttpSingleDataResponse<NotificationTemplate[]>> {
    return this.http.get<IHttpSingleDataResponse<NotificationTemplate[]>>(
      `${environment.apiGatewayBaseUrl}/notification/v1/sms/template?${queryParams}`
    );
  }

  createEmailNotificationTemplate(
    template: CreateEmailNotificationTemplateRequest
  ): Observable<IHttpSingleDataResponse<NotificationTemplateCreateResponse>> {
    return this.http.post<
      IHttpSingleDataResponse<NotificationTemplateCreateResponse>
    >(
      `${environment.apiGatewayBaseUrl}/notification/v1/email/template`,
      template
    );
  }

  createSmsNotificationTemplate(
    template: CreateSmsNotificationTemplateRequest
  ): Observable<IHttpSingleDataResponse<NotificationTemplateCreateResponse>> {
    return this.http.post<
      IHttpSingleDataResponse<NotificationTemplateCreateResponse>
    >(
      `${environment.apiGatewayBaseUrl}/notification/v1/sms/template`,
      template
    );
  }
  // Document Generation API methods
  previewCertificate(
    templateCode: string,
    parameters: Record<string, any>
  ): Observable<IHttpSingleDataResponse<string>> {
    return this.http.post<IHttpSingleDataResponse<string>>(
      `${environment.apiGatewayBaseUrl}/document-generation/v1/preview-certificate`,
      { templateCode, parameters }
    );
  }

  /**
   * Fetch template content from the backend
   */
  fetchTemplate(
    templateCode: string
  ): Observable<{ content: string; contentType: string }> {
    return this.http
      .get<IHttpSingleDataResponse<IFileDto>>(
        `${environment.apiGatewayBaseUrl}/document-generation/v1/download-template-by-code`,
        {
          headers: {
            templateCode: templateCode,
          },
        }
      )
      .pipe(
        map(response => {
          if (!response.data?.file) {
            throw new Error('No template content found');
          }

          return {
            content: atob(response.data.file),
            contentType: response.data.contentType || 'text/html', // Default to HTML if no content type
          };
        })
      );
  }

  /**
   * Detect template type from contentType
   */
  detectTemplateType(contentType: string): 'html' | 'jrxml' | 'unknown' {
    // Check content type for JRXML
    if (contentType.includes('jrxml') || contentType.includes('xml')) {
      return 'jrxml';
    }

    // Check content type for HTML
    if (contentType.includes('html')) {
      return 'html';
    }

    return 'unknown';
  }

  /**
   * Extract variables from HTML template
   */
  extractHtmlVariables(content: string): TemplateVariableInfo[] {
    const variables: TemplateVariableInfo[] = [];
    const variableRegex = /\${([^}]+)}/g;
    let match;

    // Create a temporary div to parse HTML content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    // Get all text content to search for variables
    const textContent = tempDiv.textContent || '';

    // Find all variable matches in the text content
    while ((match = variableRegex.exec(textContent)) !== null) {
      const variableName = match[1].trim();

      // Skip duplicates
      if (!variables.find(v => v.name === variableName)) {
        variables.push({
          name: variableName,
          type: this.mapJavaTypeToFormType('String'), // Default to string for HTML templates
          defaultValue: '',
        });
      }
    }

    return variables;
  }

  /**
   * Extract variables from JRXML/Jasper template
   */
  extractJrxmlVariables(jrxmlContent: string): TemplateVariableInfo[] {
    const variables: TemplateVariableInfo[] = [];
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(jrxmlContent, 'text/xml');

    // Get all parameter elements
    const parameters = xmlDoc.getElementsByTagName('parameter');

    for (let i = 0; i < parameters.length; i++) {
      const param = parameters[i];
      const name = param.getAttribute('name');
      const className = param.getAttribute('class');

      if (name) {
        variables.push({
          name: name,
          type: this.mapJavaTypeToFormType(className || 'java.lang.String'),
          defaultValue:
            param.getAttribute('defaultValueExpression') || undefined,
        });
      }
    }

    return variables;
  }

  /**
   * Map Java types to form field types
   */
  private mapJavaTypeToFormType(javaType: string): string {
    switch (javaType) {
      case 'java.lang.Integer':
      case 'java.lang.Long':
      case 'java.lang.Double':
      case 'java.lang.Float':
        return 'number';
      case 'java.util.Date':
        return 'date';
      case 'java.lang.Boolean':
        return 'boolean';
      case 'java.lang.String':
      default:
        return 'text';
    }
  }

  importTemplate(
    templateData: ImportTemplateRequest
  ): Observable<IHttpSingleDataResponse<ICertificateTemplate>> {
    const formData = new FormData();
    formData.append('organizationId', templateData.organizationId);
    formData.append('templateType', templateData.templateType);
    formData.append('templateName', templateData.templateName);
    formData.append('templateLanguage', templateData.templateLanguage);

    // Convert HTML content to a File object and append it
    const htmlBlob = new Blob([templateData.template], { type: 'text/html' });
    formData.append('template', htmlBlob, `${templateData.templateName}.html`);

    return this.http.post<IHttpSingleDataResponse<ICertificateTemplate>>(
      `${environment.apiGatewayBaseUrl}/document-generation/v1/create-template`,
      formData
    );
  }

  updateTemplate(
    templateCode: string,
    htmlContent: string
  ): Observable<IHttpSingleDataResponse<ICertificateTemplate>> {
    // Create a File object from the HTML content
    const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
    const templateFile = new File([htmlBlob], `template_${templateCode}.html`, {
      type: 'text/html',
    });

    const formData = new FormData();
    formData.append('templateCode', templateCode);
    formData.append('template', templateFile);

    return this.http.put<IHttpSingleDataResponse<ICertificateTemplate>>(
      `${environment.apiGatewayBaseUrl}/document-generation/v1/update-template-by-code`,
      formData
    );
  }

  // Field Selection State Management
  getFieldSelectionState(): FieldSelectionState {
    return this.fieldSelectionState$.value;
  }

  setFieldSelectionState(state: FieldSelectionState): void {
    this.fieldSelectionState$.next(state);
  }

  getFieldSelectionState$(): Observable<FieldSelectionState> {
    return this.fieldSelectionState$.asObservable();
  }

  // Parse form fields from FormlyFieldConfig
  parseFormFields(
    formDefinition: Record<string, FormlyFieldConfig[]>
  ): ServiceFormField[] {
    const fields: ServiceFormField[] = [];

    if (!formDefinition || typeof formDefinition !== 'object') {
      console.warn('Invalid form definition provided:', formDefinition);
      return fields;
    }

    Object.entries(formDefinition).forEach(([section, fieldConfigs]) => {
      if (!Array.isArray(fieldConfigs)) {
        console.warn(
          `Section "${section}" does not contain an array of field configs:`,
          fieldConfigs
        );
        return;
      }

      // Process each field config similar to configure-service-dynamic-pricing-form
      this.processFieldConfigs(fieldConfigs, fields, section);
    });

    return fields;
  }

  private processFieldConfigs(
    fieldConfigs: any[],
    fields: ServiceFormField[],
    section: string
  ): void {
    fieldConfigs.forEach((field: any) => {
      if (field?.fieldGroup && Array.isArray(field.fieldGroup)) {
        // Process nested field groups
        field.fieldGroup.forEach((fieldGroup: any) => {
          if (fieldGroup?.fieldGroup && Array.isArray(fieldGroup.fieldGroup)) {
            fieldGroup.fieldGroup.forEach((fg: any) => {
              if (fg?.fieldGroup && Array.isArray(fg.fieldGroup)) {
                fg.fieldGroup.forEach((element: any) => {
                  this.processIndividualField(element, fields, section);
                });
              } else {
                this.processIndividualField(fg, fields, section);
              }
            });
          } else {
            this.processIndividualField(fieldGroup, fields, section);
          }
        });
      } else {
        this.processIndividualField(field, fields, section);
      }
    });
  }

  private processIndividualField(
    element: any,
    fields: ServiceFormField[],
    section: string
  ): void {
    if (!element || !element.type) return;

    // Handle customcascadingdropdowns exactly like in configure-service-dynamic-pricing-form
    if (element.type === 'customcascadingdropdowns' && element.props?.configs) {
      element.props.configs.forEach((config: any) => {
        const configCopy = { ...config };
        configCopy['type'] = 'cascading_customdropdown';

        const field: ServiceFormField = {
          fieldName: configCopy.key,
          fieldLabel: configCopy.label || configCopy.key,
          fieldType: 'cascading_customdropdown',
          category: 'regular',
          section,
        };
        fields.push(field);
      });
      return;
    }

    // Handle customslotbooking exactly like in configure-service-dynamic-pricing-form
    if (element.type === 'customslotbooking' && element.props?.configs) {
      const slotBookingKey = element.key;
      element.props.configs.forEach((config: any) => {
        const configCopy = { ...config };
        let fieldType: string;
        let fieldName: string;

        if (config?.isSeats) {
          fieldType = 'number';
          fieldName = `${slotBookingKey}.${config.key}`;
        } else {
          fieldType = 'slot_booking_customdropdown';
          fieldName = `${slotBookingKey}.${config.key}.id`;
        }

        const field: ServiceFormField = {
          fieldName: fieldName,
          fieldLabel: configCopy.label || configCopy.key,
          fieldType: fieldType,
          category: 'regular',
          section,
        };
        fields.push(field);
      });
      return;
    }

    // Handle regular fields
    const fieldName = this.extractFieldName(element);
    const fieldLabel = this.extractFieldLabel(element);
    const fieldType = this.extractFieldType(element);

    if (fieldName && fieldLabel) {
      const category =
        fieldType === 'customfileupload' ? 'attachment' : 'regular';

      const field: ServiceFormField = {
        fieldName,
        fieldLabel,
        fieldType,
        category,
        section,
      };

      fields.push(field);
    }
  }

  private isValidFieldConfig(config: any): boolean {
    // Check various possible field structures
    return (
      config &&
      ((config.key && (config.templateOptions?.label || config.label)) ||
        (config.props?.key &&
          (config.props?.label || config.templateOptions?.label)) ||
        (config.fieldGroup && Array.isArray(config.fieldGroup)))
    );
  }

  private extractFieldName(config: any): string | null {
    return (
      config.key || config.props?.key || config.name || config.fieldName || null
    );
  }

  private extractFieldLabel(config: any): string | null {
    return (
      config.templateOptions?.label ||
      config.label ||
      config.props?.label ||
      config.templateOptions?.placeholder ||
      config.placeholder ||
      this.extractFieldName(config)
    ); // fallback to field name
  }

  private extractFieldType(config: any): string {
    const type = config.type || config.props?.type || 'input';
    return typeof type === 'string' ? type : 'input';
  }

  getServiceTemplateData(requestId: string, certificateCode: string) {
    return this.http.get<{
      templateData: any;
      fieldState: FieldSelectionState;
      language: IServiceLanguage;
      templateId: string;
    }>(
      `${environment.apiGatewayBaseUrl}/service-change-requests/${requestId}/templates/${certificateCode}`
    );
  }

  createCertificate(requestId: string, data: CertificateCreationRequest) {
    return this.http.post<CertificateCreationResponse>(
      `${environment.apiGatewayBaseUrl}/service-change-requests/${requestId}/certificates`,
      data
    );
  }

  getServiceNotificationTemplates(
    queryParams?: string
  ): Observable<IHttpSingleDataResponse<ServiceNotificationTemplates>> {
    return this.http.get<IHttpSingleDataResponse<ServiceNotificationTemplates>>(
      `${environment.apiGatewayBaseUrl}/notification/v1/service-templates?${queryParams}`
    );
  }

  enableDebugMode(
    changeRequestId: string
  ): Observable<IHttpSingleDataResponse<IServiceChangeRequest>> {
    return this.http.put<IHttpSingleDataResponse<IServiceChangeRequest>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${changeRequestId}/action/enable-debug`,
      {}
    );
  }

  disableDebugMode(
    changeRequestId: string
  ): Observable<IHttpSingleDataResponse<IServiceChangeRequest>> {
    return this.http.put<IHttpSingleDataResponse<IServiceChangeRequest>>(
      `${environment.apiGatewayBaseUrl}/service/api/v1/change-requests/${changeRequestId}/action/disable-debug`,
      {}
    );
  }
}
